<template>
  <div
    :id="`project-sr-${srIndex}`"
    class="project-sr"
  >
    <div class="row completion-type">
      <TextField
        :label="$t('project.submission.completionTypeDate')"
        :value="completionMsg"
        :readonly="true"
      />
    </div>

    <TextField
      :label="requestLabel"
      :value="submission.completedItems"
      :readonly="true"
    />
    <div>
      <InvoiceInfo
        v-if="showInvoice"
        :submission="submission"
        :srIndex="srIndex"
        :readonly="true"
      />
      <PaidStatus
        :submission="submission"
        :isContractor="isContractor"
      />
    </div>
    <div>
      <InspectorVue :submission="submission" />
      <ContractReleasedPoVue
        v-if="shouldShowPcReleasedPo"
        :submission="submission"
        :srIndex="srIndex"
      />
    </div>
    <po-balance-calculation
      :submission="submission"
      :srIndex="srIndex"
      :readonly="true"
    />
    <div
      v-if="showInvoice"
      class="project-sr-attachment"
    >
      <div>
        <label>{{ 'Invoice' }}</label>
      </div>
      <EditAttachments
        :sourceId="submission.invoice.id"
        :canDelete="false"
      />
    </div>

    <div
      v-show="hasAttachments"
      class="project-sr-attachment"
    >
      <div>
        <label>{{ $t('project.submission.supportDocs') }}</label>
      </div>
      <EditAttachments
        :sourceId="submission.id"
        :canEdit="isContractor && isDraft"
        :canDelete="false"
        :getFileLengthID="submission.id"
        :canDownloadAll="!isContractor"
        :downloadString="'Download Supporting Doc'"
        :zipName="`${currentProject.number}_Submission_attachments.zip`"
      />
    </div>

    <div class="row submission-info">
      <TextField
        :label="$t('PIC')"
        class="small-3 medium-3 large-3 columns"
        :value="personInCharge.name"
        :readonly="true"
      />
      <TextField
        :label="$t('position')"
        class="small-3 medium-3 large-3 columns"
        :value="personInCharge.position"
        :readonly="true"
      />
      <TextField
        :label="$t('onBehalfOf')"
        class="small-4 medium-4 large-4 columns"
        :value="companyName"
        :readonly="true"
      />
      <TextField
        :label="$t('submittedOn')"
        class="small-2 medium-2 large-2 columns submitted-on"
        :value="submittedDate"
        :readonly="true"
      />
    </div>

    <div class="project-sr-export clearfix">
      <div
        v-if="!isContractor && isAppr"
        class="float-right clearfix"
      >
        <button
          type="button"
          class="btn btn--darkblue"
          @click="() => exportPDF()"
        >{{ $t('action.generatePDF') }}</button>
      </div>
    </div>

    <div
      v-show="rejectHistory && rejectHistory.length > 0"
      class="action-history"
    >
      <div>
        <label>{{ $t('history') }}</label>
      </div>
      <reject-history
        v-for="(history, index) in rejectHistory"
        :key="index"
        :item="history"
        :isContractor="isContractor"
        :showRejectHeader="!index"
        :class="{'odd-row': index%2 }"
      />
    </div>

    <HKLViewLayout
      v-if="isOpen"
      :checkIsRepresentative="true"
    >
      <ProjectRepRemarks
        @review="(...args) => review(...args)"
      />
    </HKLViewLayout>
    <div
      v-if="isPicView"
      class="project-sr-approval clearfix"
    >
      <div
        v-if="!isContractor && isOpen"
        class="float-right clearfix"
      >
        <button
          type="button"
          class="btn btn--darkblue tender-sticky-button"
          @click="() => reject(submission.id)"
        >{{ $t('action.reject') }}</button>
        <button
          type="button"
          class="btn btn--orange tender-sticky-button"
          @click="() => beforeAcceptSubmission()"
        >{{ $t('action.accept') }}</button>
      </div>
    </div>
    <FinalPaymentModal
      ref="retFinalPayModal"
      :afterConfirm="openRateModalInRetention"
      :srIndex="srIndex"
      :submission="submission"
      :eventId="dialogId"
    />
    <rate
      v-if="isForceRatingSubmission"
      ref="rateModal"
      :rateType="rateCatergory"
      :dialogId="dialogId"
      :actionType="rateActionCatergory"
      :sourceId="submission.id"
      :allowEdit="isOpen"
    />
    <RejectModal
      v-if="!isContractor && isOpen"
      :id="rejectModalId"
      ref="rejectSrModal"
    >
      <div slot="header">
        <p class="modal-pure-text">
          {{ $t('project.submission.rejectAlert') }}
        </p>
      </div>
    </RejectModal>

    <HKLModal
      ref="recheckSupportDocModal"
      :title="$t('project.supportDocument.modelLabel')"
      :closable="true"
      :hasCancelBtn="true"
      okBtnAction="yesAccept"
      @onOkClick="() => acceptSubmission()"
    />
    <HKLModal
      ref="checkingHintsModal"
      :title="$t('project.submission.checkHint.title')"
      :closable="true"
      :hasCancelBtn="false"
    >
      <div class="project-sr__hints">
        <ul>
          <li
            v-for="(hint, index) in checkingHints"
            :key="index"
          >
            {{ hint }}
          </li>
        </ul>
      </div>
    </HKLModal>
    <SubmissionInspectorModal
      ref="inspectorModal"
      :submission="submission"
      :nextActionAfterSave="showPopupOrApprove"
      :submissionIndex="srIndex"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import lodash from 'lodash'
import { getCurrentInstance, computed, unref, ref } from 'vue-demi'
import EditAttachments from '@/controls/EditAttachments'
import TextField from '@/controls/TextField'
import RejectHistory from '@/modules/projects/documents/RejectHistory'
import Rate from '@/modules/projects/rate/Rate'
import RejectModal from '@/modules/projects/RejectModal'
import FinalPaymentModal from '@/modules/projects/submission-request/FinalPaymentModal'
import PoBalanceCalculation from '@/modules/projects/submission-request/PoBalanceCalculation'
import * as folderApi from '@/api/folders/request'
import { exportSubmission } from '@/api/printers/request'
import { getCompletionMsg } from '@/api/printers/project'
import { notifySubmissionAcceptance, updateInspector, updateSubmission } from '@/api/projects/request'
import { aclHelper } from '@/utils/aclHelper'
import amountUtil from '@/utils/amountUtil'
import { ActionTypes } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import EventBus from '@/utils/eventBus'
import { completionType, folderStatus, submissionStatus, rateType, rateActionType } from '@/utils/locale'
import { compareWithThreshold } from '@/utils/projectUtil'
import { isRetentionPaymentSr } from '@/modules/projects/submission-request/utils'
import HKLModal from '@/controls/Modal/HKLModal'
import { useRefModal } from '@/controls/Modal/modal.hook'
import genRandomID from '@/utils/genRandomID'
import { useStoreActions, useStoreGetters } from '@/store/index'
import { useIsProjectRepresentative, useProjectRepReviewName } from '@/pages/projects/projectInfo'
import HKLViewLayout from '@/controls/Layout/HKLViewLayout.vue'
import i18n from '@/utils/i18n'
import InvoiceInfo from '../InvoiceInfo.vue'
import SubmissionInspectorModal from '../SubmissionInspectorModal.vue'
import { PROJECT_FOLDER_STATUS, PROJECT_TYPES } from '../../consts'
import ProjectRepRemarks from '../../ProjectRepRemarks.vue'
import PaidStatus from '../PaidStatus.vue'
import ContractReleasedPoVue from '../ContractReleasedPo.vue'
import InspectorVue from '../Inspector.vue'

export default {
  name: 'SubmissionRequestView',
  components: {
    ContractReleasedPoVue,
    TextField,
    RejectHistory,
    RejectModal,
    PoBalanceCalculation,
    EditAttachments,
    Rate,
    FinalPaymentModal,
    HKLModal,
    InvoiceInfo,
    SubmissionInspectorModal,
    ProjectRepRemarks,
    HKLViewLayout,
    PaidStatus,
    InspectorVue,
  },

  props: {
    srIndex: {
      type: Number,
      default: 0,
    },
    submission: Object,
    isContractor: Boolean,
    companyName: String,
  },

  setup (props) {
    const { proxy } = getCurrentInstance()
    const { isHKlandUser } = useStoreGetters('user', ['isHKlandUser'])
    const { currentProject, purchase } = useStoreGetters(
      'projects', ['currentProject', 'purchase'],
    )
    const isProjectRepresentative = useIsProjectRepresentative()
    const { modal: checkingHintsModal, open: openCheckingHintsModal } = useRefModal()
    const checkingHints = ref([])
    let returnData = {
      currentProject,
      isProjectRepresentative,
      isHKlandUser,
      purchase,
      checkingHints,
      checkingHintsModal,
    }
    const getRemainingBalance = () => {
      const poAmount = lodash.get(unref(purchase), 'amount', 0)
      const completedAmount = lodash.get(unref(currentProject), 'completedAmount', 0)
      const deductionAmount = lodash.get(unref(currentProject), 'deduction.amount', 0)
      const remainingBalance = amountUtil.format(poAmount - deductionAmount - completedAmount)
      return remainingBalance
    }
    const hasUnapprovedFolders = async () => {
      const folders = await folderApi.getFolders(unref(currentProject).id)
      let hasUnapproved = false
      folders.forEach((folder) => {
        if (folder.status !== folderStatus.APPR) {
          hasUnapproved = true
        }
      })
      return hasUnapproved
    }
    const acceptSubmission = async () => {
      if (unref(showInspectorPopup)) {
        openInspectorModal()
        return
      }

      await proxy.showPopupOrApprove()
    }
    returnData = {
      ...returnData,
      getRemainingBalance,
      acceptSubmission,
    }
    // #region ActionHistory
    const rejectHistory = computed(() => {
      return ((props.submission || {}).actionHistory || [])
        .filter(history => history.actionType)
        .map(history => {
          if (history.actionType !== PROJECT_FOLDER_STATUS.REVIEW) return history
          if (unref(isHKlandUser) || unref(isProjectRepresentative)) {
            return lodash.defaultsDeep({
              approver: history.reviewer,
              detail: {
                rejectReason: history.detail.remarks,
              },
            }, history)
          }
          return void 0
        })
        .filter(Boolean)
    })
    returnData = {
      ...returnData,
      rejectHistory,
    }
    // #endregion ActionHistory
    // #region Constants
    const rejectModalId = computed(() => `rejectSr${genRandomID()}`)
    const dialogId = computed(() => `project-work-completed-dialog${genRandomID()}`)
    returnData = {
      ...returnData,
      rejectModalId,
      dialogId,
    }
    // #endregion Constants
    // #region Inspector Modal
    const { backEndRegionalEnv } = useStoreGetters('regions', ['backEndRegionalEnv'])
    const { modal: inspectorModal, open: openInspectorModal } = useRefModal()
    // For historical data no invoice handling
    const showInvoice = computed(() => {
      const hasInvoiceNum = lodash.get(props.submission, 'invoice.invoiceNumber') || null
      return (hasInvoiceNum || proxy.isDraft) && lodash.get(unref(backEndRegionalEnv), 'moduleSwitch.submissionInvoiceModule')
    })
    const showInspectorPopup = computed(() => {
      const userInspectionAmount = lodash.get(unref(currentUser), 'projectInspectionAmount')
      const isPO = lodash.get(unref(currentProject), 'numberType') === PROJECT_TYPES.PO
      const isOverInspectorThreshold = Number(unref(purchase).amountBase) > Number(userInspectionAmount)
      return unref(showInvoice) && isPO && userInspectionAmount !== null && isOverInspectorThreshold
    })
    const shouldShowPcReleasedPo = computed(() => {
      const isContract = lodash.get(unref(currentProject), 'numberType') === PROJECT_TYPES.CONTRACT
      const isAllowedUser = unref(isProjectRepresentative) || unref(isHKlandUser)
      return isAllowedUser && isContract
    })
    returnData = {
      showInvoice,
      ...returnData,
      inspectorModal,
      openInspectorModal,
      showInspectorPopup,
      shouldShowPcReleasedPo,
    }
    // #endregion
    // #region Check Support Documents
    const hasAttachments = ref(false)
    const { modal: recheckSupportDocModal, open: openRecheckSupportDocModal } = useRefModal()
    const beforeAcceptSubmission = async () => {
      checkingHints.value = []
      const isPoOverThreshold = await compareWithThreshold(unref(purchase))
      const nonActualCommDate = !unref(currentProject).actualCommDate
      const showActualCommDateHint = isPoOverThreshold && nonActualCommDate

      const remainingBalance = getRemainingBalance()
      const showRemainingBalanceHint = parseFloat(props.submission.completedAmount) > Number(remainingBalance)

      const notPartialType = props.submission.completionType !== completionType.PARTIAL
      const hasUnapproved = await hasUnapprovedFolders()
      const showAllDocsHint = notPartialType && hasUnapproved

      const releasedPo = lodash.get(props.submission, 'releasedPo', '')
      const showReleasedPoHint = lodash.isEmpty(releasedPo) && unref(shouldShowPcReleasedPo)
      const keyPrefix = 'project.submission.checkHint.fields'
      if (showActualCommDateHint) {
        checkingHints.value.push(i18n.t(`${keyPrefix}.actualCommencementDate`))
      }
      if (showRemainingBalanceHint) {
        checkingHints.value.push(i18n.t(`${keyPrefix}.remainingBalance`))
      }
      if (showAllDocsHint) {
        checkingHints.value.push(i18n.t(`${keyPrefix}.allDocumentFolders`))
      }
      if (showReleasedPoHint) {
        checkingHints.value.push(i18n.t(`${keyPrefix}.releasedPO`))
      }
      if (unref(checkingHints).length > 0) {
        openCheckingHintsModal()
        return
      }

      if (unref(hasAttachments)) {
        return acceptSubmission()
      }
      openRecheckSupportDocModal()
    }
    returnData = {
      ...returnData,
      hasAttachments,
      recheckSupportDocModal,
      beforeAcceptSubmission,
    }
    // #endregion Check Support Documents
    // #region Remarks
    const { currentUser } = useStoreGetters('user', ['currentUser'])
    const { loadProject } = useStoreActions('projects', ['loadProject'])
    const curUserReviewName = useProjectRepReviewName()
    const review = async (remarksContent, { remarks }) => {
      const name = unref(curUserReviewName)
      const actionHistory = {
        actionType: PROJECT_FOLDER_STATUS.REVIEW,
        detail: { remarks: remarksContent || '' },
        reviewer: {
          id: unref(currentUser).id,
          name,
          username: unref(currentUser).username,
        },
      }
      await updateSubmission({ id: proxy.submission.id, actionHistory })
      remarks.value = ''
      await loadProject(unref(currentProject).id)
    }
    returnData = {
      ...returnData,
      review,
    }
    // #endregion Remarks
    return returnData
  },

  data () {
    return {
      rateType: rateType.WORK_COMPLETED,
      actionType: rateActionType.WORK_COMPLETED,
    }
  },

  computed: {
    ...mapGetters('user', [
      'currentUser',
    ]),
    ...mapGetters('projects', ['hasRetention']),
    requestLabel () {
      return this.isContractor
        ? this.$t('project.submission.vendorCompletedItems')
        : this.$t('project.submission.hklCompletedItems')
    },

    completionMsg () {
      return getCompletionMsg(this.submission)
    },
    submittedDate () {
      const date = (this.submission || {}).submittedDate
      if (!date) {
        return ''
      }
      return dateUtil.formatDate(date, true)
    },
    personInCharge () {
      return (this.submission || {}).personInCharge
    },
    isDraft () {
      return (this.submission || {}).status === submissionStatus.DRAFT
    },
    isOpen () {
      return (this.submission || {}).status === submissionStatus.OPEN
    },
    isAppr () {
      return (this.submission || {}).status === submissionStatus.APPR
    },
    isPicView () {
      return aclHelper.isProjectPic()
    },
    rateCatergory () {
      return this.rateType
    },
    rateActionCatergory () {
      return this.actionType
    },
    isForceRatingSubmission () {
      return !this.isContractor && this.isOpen && lodash.get(this.submission, 'completionType') !== completionType.PARTIAL
    },
  },
  mounted () {
    EventBus.$on(`modules_projects_rejectModal_doReject_${this.rejectModalId}`, (payload) => {
      this.rejectSubmission(submissionStatus.REJECTED, payload.actionHistory)
      EventBus.$emit(`modules_projects_rejectModal_doClose_${this.rejectModalId}`)
    })
    this.$on(`controls.hkldDropzone.loadFilesLength${this.submission.id}`, (filesCount) => {
      if (filesCount > 0) {
        this.hasAttachments = true
      }
    })
    EventBus.on(`rate_or_final_modal_update_submission_${this.dialogId}`, () => {
      this.approveSubmission()
    })
  },
  destroyed () {
    this.$off(`controls.hkldDropzone.loadFilesLength${this.submission.id}`)
    EventBus.$off(`modules_projects_rejectModal_doReject_${this.rejectModalId}`)
    EventBus.$off(`rate_or_final_modal_update_submission_${this.dialogId}`)
  },
  methods: {
    ...mapActions('projects', [
      'updateSubmissionStatus',
      'saveSubmission',
      'updateProject',
      'loadProject',
    ]),
    async showPopupOrApprove () {
      const remainingBalance = this.getRemainingBalance()
      const type = this.submission.completionType

      if (this.isFinalSubmissionRequest()) {
        this.$refs.rateModal.init()
      } else if (isRetentionPaymentSr(type)) {
        const remainingBalanceIfAccept = Number(remainingBalance - this.submission.completedAmount)
        const retentionDirectly = remainingBalanceIfAccept === 0 || (!this.submission.retentionOrder && this.submission.completionType !== completionType.RETENTION_PAYMENT)
        retentionDirectly ? this.openRateModalInRetention() : this.$refs.retFinalPayModal.open()
      } else {
        await this.approveSubmission()
      }
    },

    async rejectSubmission (status, actionHistory) {
      const action = ActionTypes.submissions.reject
      await this.updateSubmissionAndEmail(this.submission.id, {
        status,
        actionHistory,
        action,
        emailAction: this.$i18n.t('rejected'),
      })
    },

    async updateInspected (inspectedById) {
      const request = {
        id: inspectedById,
      }
      await updateInspector(this.submission.id, request)
    },
    async approveSubmission () {
      if (this.showInspectorPopup) {
        const inspectedById = lodash.get(this.submission, 'invoice.inspectedBy', '')
        await this.updateInspected(inspectedById)
      }
      const actionHistory = {
        approver: {
          id: this.currentUser.id,
          name: this.currentUser.displayName || this.currentUser.name,
          username: this.currentUser.username,
        },
        actionType: this.$i18n.t('action.accept').toUpperCase(),
      }
      const status = submissionStatus.APPR
      const action = ActionTypes.submissions.accept
      await this.updateSubmissionAndEmail(this.submission.id, {
        status,
        actionHistory,
        action,
        emailAction: this.$i18n.t('accepted'),
      }, true)
    },
    openRateModalInRetention () {
      this.rateType = rateType.RETENTION_RECEIVED
      this.actionType = rateActionType.RETENTION_RECEIVED
      this.$refs.rateModal.init()
    },

    isFinalSubmissionRequest () {
      if ([completionType.FULL, completionType.FINAL_PARTIAL].includes(this.submission.completionType)) {
        return true
      }
      return false
    },
    reject (srId) {
      this.$refs.rejectSrModal.init(srId)
    },

    async updateSubmissionAndEmail (id, values, showEmailNote = false) {
      const { emailAction, ...restValues } = values
      const projectId = this.currentProject.id
      await this.updateSubmissionStatus({ id, ...restValues })
      await this.loadProject(projectId)
      EventBus.$emit('refresh:project-submission-request')
      await notifySubmissionAcceptance({
        order: this.submission.order,
        action: emailAction,
        showNote: showEmailNote,
      })
    },

    async exportPDF () {
      await exportSubmission(this.currentProject, this.submission.order)
    },
  },

}
</script>

<style lang="scss" scoped>
@import '~styles/project/style';
@import '~styles/popup';

.completion-type .text-field {
  margin-right: 15px;
}
.final-partial {
  margin-bottom: 0px;
  vertical-align: middle;
  transform: translate(0, 40%);
}
.project-sr-attachment, .project-sr-approval {
  margin-top: 20px;
  overflow: auto;
}
.project-sr-export {
  padding-bottom: 10px;
}
.project-sr-approval .btn {
  margin-left: 8px;
  margin-right: 8px;
}
.submission-info {
  margin-bottom: 10px;
}
.action-history {
  border-top: 1px solid #e1e1e1;
  padding-top: 10px;
}
.projects-po-balance-cal-view {
  margin-top: 20px;
}
</style>

<style lang="scss">
.project-sr {
  .text-field {
    label {
      margin-top: 0;
    }
    pre p {
      margin-bottom: 0;
    }
  }
  .submission-info .submitted-on {
    label, pre p{
      text-align: right;
    }
  }

  .edit-attachments {
    .number {
      text-align: left;
    }
    th.number {
      padding-left: 0.625rem;
    }
  }
}
</style>
